import { FC, useEffect, useRef } from 'react';

import CustomSelect from 'components/custom-select';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import { CicloDoRecorte, useCicloDaTela } from 'hooks/useCicloDaTela';

interface TitleTabOptions {
  title: JSX.Element;
  /** Tema do campo; `light` é o do rebranding. */
  variant?: 'dark' | 'light';
  hideFilter?: boolean;
  /** Ciclos da tela (o recorte dela), do mais recente ao mais antigo. */
  options: any;
  className?: string;
  /** O ciclo da tela, com `value` (o `_id`) e `label` (o título). Só é chamado com um ciclo do recorte. */
  onCallBack: (cycle: any) => void;
  hideLabel?: boolean;
}

/**
 * Título com o seletor de ciclo da tela. O ciclo é o do app (CIC-10, `useCicloDaTela`): a tela acompanha a troca
 * feita no menu e a troca feita aqui vale para o app. Fora do recorte, o seletor fica sem ciclo e a tela mostra o
 * aviso (CIC-12), com o mesmo hook.
 */
const TabTitle: FC<TitleTabOptions> = ({ title, options, onCallBack, hideLabel, variant = 'dark' }: TitleTabOptions) => {
  const ciclos: (CicloDoRecorte & Record<string, any>)[] = options ?? [];
  const { ciclo, trocar } = useCicloDaTela(ciclos);

  // A tela recria o `onCallBack` a cada render: a referência guarda o mais recente sem reavisar a tela.
  const avisarTela = useRef(onCallBack);
  useEffect(() => {
    avisarTela.current = onCallBack;
  });

  // `ciclo` é um item da lista da tela: muda quando o ciclo do app muda (ou a lista é refeita), não a cada render.
  useEffect(() => {
    if (ciclo) avisarTela.current({ ...ciclo, value: ciclo._id, label: ciclo.title });
  }, [ciclo]);

  const opcoesDoSelect = ciclos.map(item => ({ label: item.title, value: item._id }));

  // PAD-12: nas telas da nova identidade (`light`), o seletor de ciclo único, com os ciclos rotulados pelo status.
  // A variante `dark` segue nas telas antigas, só com o título.
  const campoDeCiclo =
    variant === 'light' ? (
      <SeletorDeCiclo ciclos={ciclos} valor={ciclo?._id} onChange={trocar} />
    ) : (
      <CustomSelect
        variant={variant}
        placeholder="Clique para selecionar o ciclo"
        options={opcoesDoSelect}
        onChange={opcao => opcao && trocar(String(opcao.value))}
        value={opcoesDoSelect.find(opcao => opcao.value === ciclo?._id) ?? null}
      />
    );

  return (
    <>
      {!hideLabel ? (
        <div className="flex flex-col items-start justify-start mb-8 lg:flex-row lg:justify-between lg:items-center lg:mb-14">
          <div className="flex flex-col lg:flex-row lg:items-center mb-8 lg:mb-0 lg:space-x-4" tabIndex={0}>
            <h3 className="block text-[22px] mb-3 lg:mb-0" >
              {title}
            </h3>
            {!!ciclo && (
              <>
                <span className="text-white text-[22px] hidden lg:block">|</span>
                <span className="block text-yellow font-normal text-[22px]">
                  <>Ciclo {ciclo.title}</>
                </span>
              </>
            )}
          </div>

          {options && (
            <div className="w-full lg:w-[264px]">
            {campoDeCiclo}
          </div>
        )}
        </div>
      ) : (
        <>
          {options && (
            <div className="w-full lg:w-[264px]">
              {campoDeCiclo}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TabTitle;
